import { invoke } from '@shared/easyfy.decorators';
import { elementsOf, globalEvent } from '@shared/easyfy.core';
import { addCss, replaceHtml, toggleClass } from '@shared/easyfy.utils';
import { animationEnded, createElementWithClasses, createLink, isMobile, updateHashParam, updateHashParamForUrl } from '@clientShared/utils';
import EasyfyModules from '@shared/easyfy.events/easyfy.eventhandler';
import { logCustomMessage } from '@shared/easyfy.logger';
import { HayppEventNames } from '@clientWebModules/haypp-event-names';

const CLOSE_KEYS = ['Escape', 'Enter', ' ', 'Spacebar'];

@invoke
export default class SalesUnitSelector {
  static Instance: SalesUnitSelector;
  private isKeyboardNavigation = false;
  private currentlyOpenPopup: HTMLElement | null = null;
  private openerElement: HTMLElement | null = null;

  addSalesUnitCodeHash = (clickedSalesUnit: SalesUnitElement | null) => {
    if (!clickedSalesUnit) return;

    const el = clickedSalesUnit.element;
    if (this.isProductPage(el)) {
      updateHashParam('su', clickedSalesUnit.salesUnitCode);
    }

    if (this.isProductCard(el)) {
      this.setSelectedProductAnchorAndUpdateHash('su', el, clickedSalesUnit);
    }
  };

  private setSelectedProductAnchorAndUpdateHash = (hashKey: string, el: HTMLElement, clickedSalesUnit: SalesUnitElement) => {
    const anchorElements = el.closest('[data-container="product-card"]')?.querySelectorAll('a');
    anchorElements?.forEach((anchorEl) => {
      if (anchorEl?.href) {
        const salesUnitCode = clickedSalesUnit.salesUnitCode;
        updateHashParamForUrl(hashKey, anchorEl, salesUnitCode);
      }
    });
  };

  onInit() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: SALES_UNITS_OPEN,
          callback: this.openSelector,
        },
        {
          event: SALES_UNITS_CLOSE,
          callback: this.closeSelector,
        },
        {
          callback: this.closeSelector,
        },
      ],
    });
    globalEvent({
      type: 'change',
      events: [
        {
          event: 'sales-unit-select',
          callback: this.selectSalesUnitFromEvent,
        },
      ],
    });
    document.addEventListener(SALES_UNITS_CLOSE, () => {
      this.closeSelector();
    });
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        // Prevent packselector from closing when selection changes with keyboard
        this.isKeyboardNavigation = true;
        setTimeout(() => {
          this.isKeyboardNavigation = false;
        }, 0);
      }
    });
  }

  // ================================
  // Open, close and select
  // ================================

  openSelector = (e: Event) => {
    this.closeSelector();

    const currentSalesUnitDisplay: HTMLElement = e?.target as HTMLElement;
    const activeProductCard: HTMLElement | null = this.getActiveProductCard(currentSalesUnitDisplay);
    const salesUnitPopup: HTMLElement | null = activeProductCard ? this.getSalesUnitPopup(activeProductCard) : null;
    const availableSalesUnits: HTMLElement | null = activeProductCard ? this.getAvailableSalesUnits(activeProductCard) : null;
    const isCompactProductCard: boolean = activeProductCard ? this.getIsCompactProductCard(activeProductCard) : false;

    if (!activeProductCard || !salesUnitPopup || !availableSalesUnits || !currentSalesUnitDisplay) return console.error('Could not open sales unit selector');

    if (isCompactProductCard) {
      this.openCompactCard(currentSalesUnitDisplay, activeProductCard, salesUnitPopup);
    } else {
      this.openRegularCard(currentSalesUnitDisplay, activeProductCard, salesUnitPopup, availableSalesUnits);
    }

    const openPopup = this.getOpenPopupForInteractions(salesUnitPopup, isCompactProductCard);
    const selectedRadio = openPopup.querySelector<HTMLInputElement>('[data-selected] input[type="radio"]');
    requestAnimationFrame(() => selectedRadio?.focus({ preventScroll: true }));

    this.currentlyOpenPopup = openPopup;
    this.openerElement = (currentSalesUnitDisplay.closest('[data-container="current-salesunit-info"]') as HTMLElement | null) ?? currentSalesUnitDisplay;

    openPopup.addEventListener('keydown', this.handleOpenSelectorKeydown);
    openPopup.addEventListener('focusout', this.handleSelectorFocusOut);
  };

  private handleOpenSelectorKeydown = (e: KeyboardEvent) => {
    if (CLOSE_KEYS.includes(e.key)) {
      e.preventDefault();
      this.closeSelector();
    }
  };

  private handleSelectorFocusOut = (event: FocusEvent) => {
    if (!this.currentlyOpenPopup || !(event.relatedTarget instanceof Node)) return;
    if (!this.currentlyOpenPopup.contains(event.relatedTarget)) {
      this.closeSelector(undefined, isMobile());
    }
  };

  closeSelector = (e?: Event, restoreFocus: boolean = true) => {
    const target = e?.target as HTMLElement | undefined;
    const activeProductCard = this.getOpenSalesUnitProductCardContainer();
    const isCompactProductCard = this.getIsCompactProductCard(activeProductCard);

    if (!activeProductCard || (target && target.hasAttribute('salesunit-open'))) return;

    this.removeOpenSelectorListeners();

    if (isCompactProductCard) {
      this.closeCompactCard(activeProductCard, restoreFocus);
    } else {
      this.closeRegularCard(activeProductCard, restoreFocus);
    }
  };

  private removeOpenSelectorListeners = () => {
    if (this.currentlyOpenPopup) {
      this.currentlyOpenPopup.removeEventListener('keydown', this.handleOpenSelectorKeydown);
      this.currentlyOpenPopup.removeEventListener('focusout', this.handleSelectorFocusOut);
      this.currentlyOpenPopup = null;
    }
  };

  private restoreFocusToOpener = () => {
    this.openerElement?.focus();
  };

  selectSalesUnitFromEvent = (e: Event) => {
    const target: HTMLElement = e?.target as HTMLElement;
    if (!target) return; // add snackbar

    const clickedSalesUnit = this.getClickedSalesUnit(target);
    // Read before selectSalesUnit moves data-selected onto the clicked option.
    const wasAlreadySelected = clickedSalesUnit.element.hasAttribute('data-selected');
    this.selectSalesUnit(clickedSalesUnit, target);
    if (!wasAlreadySelected) this.trackPackSizeSelection(clickedSalesUnit);
  };

  // Deliberately not called from selectSalesUnitBySalesUnitCode, which replays the #su hash already tracked on the page that set it.
  private trackPackSizeSelection = (clickedSalesUnit: SalesUnitElement) => {
    const selector = clickedSalesUnit.element.closest<HTMLElement>('[data-sku]');
    const sku = selector?.dataset.sku;
    const defaultSalesUnitCode = selector?.dataset.defaultSalesUnitCode;
    const salesUnitCode = clickedSalesUnit.salesUnitCode;

    if (!sku || !salesUnitCode || !defaultSalesUnitCode) return;

    EasyfyModules.EasyfyEventHandler.RunEvent({
      eventName: HayppEventNames.SelectPackSize,
      data: { productIdentifier: sku, salesUnitCode, defaultSalesUnitCode },
    }).catch((error) => {
      logCustomMessage('Failed to send select pack size event', {
        action: 'trackPackSizeSelection',
        productIdentifier: sku,
        salesUnitCode,
        defaultSalesUnitCode,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? (error.stack ?? '') : '',
      });
    });
  };

  public selectSalesUnitBySalesUnitCode(salesunitcode: string) {
    const salesUnitSelectorEl = document.querySelector('#salesunit-selector') as HTMLElement;
    const target = salesUnitSelectorEl.querySelector(`[data-salesunitcode="${salesunitcode}"]`) as HTMLElement;
    const clickedSalesUnit = this.getClickedSalesUnit(target);
    this.selectSalesUnit(clickedSalesUnit, target);
  }

  private selectSalesUnit = (clickedSalesUnit: SalesUnitElement, target: HTMLElement) => {
    this.addSalesUnitCodeHash(clickedSalesUnit);

    const productContainer: HTMLElement | null = this.getOpenSalesUnitProductCardContainer() ?? target.closest('[data-container="product"]');
    const activeProductCard: HTMLElement | null = this.getActiveProductCard(target);
    if (!productContainer) return console.error('Product container is missing');
    const isCompactProductCard: boolean = this.getIsCompactProductCard(activeProductCard);

    this.removeCurrentlySelectedSalesUnit(clickedSalesUnit.element);
    clickedSalesUnit.element?.setAttribute('data-selected', '');
    const radioInput = clickedSalesUnit.element?.querySelector('input[type="radio"]') as HTMLInputElement;
    if (radioInput) {
      radioInput.checked = true;
    }
    this.updateBuyButtonOnSelect(clickedSalesUnit, productContainer);
    this.updateFlagVisibility(clickedSalesUnit.salesUnitCode, productContainer);

    if (this.isProductPage(clickedSalesUnit.element)) {
      this.updateProductPageHtmlOnSelect(clickedSalesUnit, productContainer);
    } else {
      this.updateProductCardHtmlOnSelect(clickedSalesUnit, activeProductCard, isCompactProductCard);
    }

    const useInlineSalesUnitMenu = this.useInlineSalesUnitMenuOnMobile(activeProductCard);
    if ((!isMobile() || isCompactProductCard || useInlineSalesUnitMenu) && !this.isKeyboardNavigation) this.closeSelector();
  };

  // ================================
  // Helpers when opening selector
  // ================================

  openCompactCard = (currentSalesUnitDisplay: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement | null) => {
    toggleClass(activeProductCard, 'salesunit-open');
    salesUnitPopup?.setAttribute('salesunit-open', '');
    this.addEvent(currentSalesUnitDisplay, SALES_UNITS_CLOSE);
  };

  openRegularCard = (currentSalesUnitDisplay: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement, availableSalesUnits: HTMLElement) => {
    if (isMobile() && !this.useInlineSalesUnitMenuOnMobile(activeProductCard)) {
      this.wrapProductInfoWithLink(activeProductCard, salesUnitPopup);
      this.createMobileSlideUp(activeProductCard, salesUnitPopup);
    } else {
      addCss(availableSalesUnits, 'desktop');
      toggleClass(activeProductCard, 'salesunit-open');
      salesUnitPopup.setAttribute('salesunit-open', '');
    }
  };

  addEvent = (element: HTMLElement, event: string) => {
    if (!element || !event) return console.error('Could not add event to element');

    element.dataset.event = event;
  };

  private getOpenPopupForInteractions = (salesUnitPopup: HTMLElement, isCompactProductCard: boolean): HTMLElement => {
    const activeProductCard = this.getActiveProductCard(salesUnitPopup);
    if (isMobile() && !isCompactProductCard && !this.useInlineSalesUnitMenuOnMobile(activeProductCard)) {
      const mobileContainer = document.querySelector<HTMLElement>('[data-salesunitselector-mobile-container]');
      const mobilePopup = mobileContainer ? this.getSalesUnitPopup(mobileContainer) : null;
      return mobilePopup ?? salesUnitPopup;
    }

    return salesUnitPopup;
  };

  createMobileSlideUp = (activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    let salesUnitsContainer: HTMLElement | null = document.querySelector('[data-salesunitselector-mobile-container]');

    if (!salesUnitsContainer) {
      salesUnitsContainer = this.createSalesUnitsMobileContainer();
    } else {
      salesUnitsContainer.textContent = '';
      salesUnitsContainer.classList.add('salesunit-open');
    }

    this.updateAvailableSalesUnits(salesUnitsContainer, activeProductCard, salesUnitPopup);

    document.body.appendChild(salesUnitsContainer);

    document.body.classList.add('noof');
    document.body.classList.add('salesunit-overlay-active');
  };

  createSalesUnitsMobileContainer = (): HTMLElement => {
    const salesUnitsContainer = createElementWithClasses('div', ['salesunitselector-mobile-container', 'productcard-regular', 'salesunit-open']);
    salesUnitsContainer.dataset.container = 'product-card';
    salesUnitsContainer.setAttribute('data-salesunitselector-mobile-container', '');
    return salesUnitsContainer;
  };

  updateAvailableSalesUnits = (salesUnitsContainer: HTMLElement, activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    const salesUnitPopupClone = this.cloneSalesUnitPopup(salesUnitPopup);

    this.copyEventTrackingAttributes(salesUnitsContainer, activeProductCard);
    this.appendButtonIfExists(salesUnitPopupClone, activeProductCard);

    salesUnitsContainer?.appendChild(salesUnitPopupClone);
  };

  copyEventTrackingAttributes = (salesUnitsContainer: HTMLElement, activeProductCard: HTMLElement): void => {
    Array.from(activeProductCard.attributes).forEach((attr) => {
      if (attr.name.startsWith('data-customevent-') || attr.name.startsWith('data-event-property-')) {
        salesUnitsContainer.setAttribute(attr.name, attr.value);
      }
    });
  };

  cloneSalesUnitPopup(salesUnitPopup: HTMLElement): HTMLElement {
    const salesUnitPopupClone = salesUnitPopup.cloneNode(true) as HTMLElement;
    salesUnitPopupClone?.setAttribute('salesunit-open', '');
    salesUnitPopupClone?.removeAttribute('data-salesunit-closing');
    return salesUnitPopupClone;
  }

  appendButtonIfExists(salesUnitPopupClone: HTMLElement, activeProductCard: HTMLElement) {
    const button = this.getProductCardButton(activeProductCard);
    if (button) salesUnitPopupClone?.insertAdjacentHTML('beforeend', button.outerHTML);
  }

  wrapProductInfoWithLink = (activeProductCard: HTMLElement, salesUnitPopup: HTMLElement) => {
    if (!activeProductCard || !salesUnitPopup) return;

    const url = (activeProductCard.querySelector('[data-container="product-link"]') as HTMLAnchorElement | null)?.href;
    if (!url) return;

    const productInfo = salesUnitPopup.querySelector('[data-container="product-info"]') as HTMLElement | null;
    if (!productInfo?.parentNode) return;

    const newLink = createLink(url, productInfo.innerHTML, 'product-info');
    newLink.dataset.container = 'product-info';

    productInfo.parentNode.replaceChild(newLink, productInfo);
  };

  getProductCardButton(activeProductCard: HTMLElement): HTMLElement | null {
    return (
      activeProductCard?.querySelector('[data-container="addtocart-btn"]') ??
      activeProductCard?.querySelector('[data-container="view-product"]') ??
      activeProductCard?.querySelector('[data-action="subscribe"]')
    );
  }

  // ================================
  // Helpers selecting sales unit
  // ================================

  getClickedSalesUnit = (clickedSalesUnit: HTMLElement): SalesUnitElement => {
    const labelElement = (clickedSalesUnit.closest('label[data-container="salesunit-option"]') as HTMLElement) || clickedSalesUnit;

    const packsizeElement = labelElement.querySelector('[data-container="packsize"]');
    const unitPriceElement = labelElement.querySelector('[data-container="unit-price"]');

    return {
      element: labelElement,
      productId: labelElement.dataset.value ?? '',
      salesUnitCode: labelElement.dataset.salesunitcode ?? '',
      salesUnit: Number(packsizeElement?.getAttribute('data-packsize')) || 0,
      unitPrice: unitPriceElement?.innerHTML ?? '',
      upsellId: labelElement.dataset.upsellid ?? '',
    };
  };

  isProductPage = (clickedSalesUnit: HTMLElement): boolean => {
    return !!clickedSalesUnit.closest('[data-container="product"]');
  };

  isProductCard = (clickedSalesUnit: HTMLElement): boolean => {
    return !!clickedSalesUnit.closest('[data-container="product-card"]');
  };

  removeCurrentlySelectedSalesUnit = (element: HTMLElement) => {
    if (!element?.parentElement) return console.error('Parent element is missing');
    const selectedVariant: HTMLElement = elementsOf(element?.parentElement, '[data-container="salesunit-option"][data-selected]');
    selectedVariant?.removeAttribute('data-selected');
  };

  updateBuyButtonOnSelect = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement) => {
    const buyButton = this.getActiveBuyButton(clickedSalesUnit, productContainer);

    if (!buyButton || !clickedSalesUnit.productId) return console.error('Error updating buy button');

    buyButton.dataset.productid = clickedSalesUnit.productId;
    buyButton.dataset.salesunitcode = clickedSalesUnit.salesUnitCode;
    buyButton.dataset.upsellid = clickedSalesUnit.upsellId;
  };

  getActiveBuyButton = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement): HTMLElement | null => {
    const isProductPage = this.isProductPage(clickedSalesUnit.element);
    const selector = isProductPage ? productContainer : document.querySelector('[salesunit-open]')?.closest('[data-container="product-card"]');
    return selector?.querySelector('[data-container="addtocart-btn"]') ?? null;
  };

  updateProductCardHtmlOnSelect = (clickedSalesUnit: SalesUnitElement, activeProductCard: HTMLElement | null, isCompactProductCard: boolean) => {
    if (!activeProductCard) return console.error('Active product card is missing');

    const currentSalesUnit: HTMLElement = elementsOf(activeProductCard, '[data-container="current-salesunit-info"]');
    const priceContainer: HTMLElement = elementsOf(activeProductCard, '[data-container="price-container"]');

    if (!currentSalesUnit || !activeProductCard || !clickedSalesUnit.element || !priceContainer) return;

    if (isMobile() && !isCompactProductCard && !this.useInlineSalesUnitMenuOnMobile(activeProductCard)) return;

    currentSalesUnit.innerHTML = clickedSalesUnit.element.innerHTML;
    currentSalesUnit.querySelector('input[type="radio"]')?.remove();
    currentSalesUnit.querySelector('div.radio')?.remove();

    priceContainer.setAttribute('data-unitprice', clickedSalesUnit.unitPrice);
    priceContainer.setAttribute('data-packsize', String(clickedSalesUnit.salesUnit));

    if (isCompactProductCard) {
      const price: HTMLElement = elementsOf(currentSalesUnit, '.price');
      const pricehtml = `<span class="price"><span data-container="price">${price?.innerHTML}</span><i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(price, pricehtml);
      const packsize: HTMLElement = elementsOf(currentSalesUnit, '.packsize');
      packsize?.setAttribute('data-testid', 'current-packsize');
    } else {
      const packsize: HTMLElement = elementsOf(currentSalesUnit, '.packsize');
      const packsizehtml = `<span class="packsize" data-testid="current-packsize">${packsize?.innerHTML}<i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(packsize, packsizehtml);
    }
  };

  updateProductPageHtmlOnSelect = (clickedSalesUnit: SalesUnitElement, productContainer: HTMLElement) => {
    if (!clickedSalesUnit?.element || !productContainer) return console.error('Element is missing');
    const currentSalesUnitDisplay: HTMLElement = elementsOf(productContainer, '[data-container="current-salesunit-info"]');

    if (!currentSalesUnitDisplay) return console.error('Current sales unit display is missing');

    const selectedSalesUnitText: string = elementsOf(clickedSalesUnit.element, '.packsize')?.textContent ?? '';
    const selectedPriceText: string = elementsOf(clickedSalesUnit.element, '.price')?.textContent ?? '';
    const displayPrice: HTMLElement = elementsOf(currentSalesUnitDisplay, '[data-container="selected-price"]');
    const displaySalesUnit: HTMLElement = elementsOf(currentSalesUnitDisplay, '[data-container="selected-salesunit"]');

    if (!displayPrice || !displaySalesUnit || !selectedSalesUnitText || !selectedPriceText) return console.error('Display price or sales unit is missing');

    displayPrice.textContent = selectedPriceText;
    displaySalesUnit.textContent = selectedSalesUnitText;
  };

  updateFlagVisibility = (clickedSalesUnitCode: string, productContainer: HTMLElement) => {
    const flags: NodeListOf<HTMLElement> = productContainer?.querySelectorAll('.flag[data-salesunit]');

    if (!flags || !clickedSalesUnitCode) return;

    const currentVisibleFlags = Array.from(flags).filter((flag) => flag.hasAttribute('data-current'));
    currentVisibleFlags.forEach((flag) => {
      flag.removeAttribute('data-current');
    });

    const newVisibleFlags = Array.from(flags).filter((flag) => flag.getAttribute('data-salesunit') === clickedSalesUnitCode);
    newVisibleFlags.forEach((flag) => {
      flag.setAttribute('data-current', 'true');
    });
  };

  // ================================
  // Helpers when closing selector
  // ================================

  closeCompactCard = (activeProductCard: HTMLElement, restoreFocus: boolean = true) => {
    const selectedVariant: HTMLElement | null = activeProductCard?.querySelector('[data-container="current-salesunit-info"]');
    const salesUnitPopup: HTMLElement | null = this.getSalesUnitPopup(activeProductCard);

    salesUnitPopup?.removeAttribute('salesunit-open');
    activeProductCard.classList.remove('salesunit-open');
    if (selectedVariant) this.addEvent(selectedVariant, SALES_UNITS_OPEN);
    if (restoreFocus) {
      this.restoreFocusToOpener();
    }
  };

  closeRegularCard = (activeProductCard: HTMLElement, restoreFocus: boolean = true) => {
    const container: HTMLElement | null =
      isMobile() && !this.useInlineSalesUnitMenuOnMobile(activeProductCard) ? document.querySelector('.salesunitselector-mobile-container') : activeProductCard;
    if (!container) {
      this.openerElement = null;
      return;
    }
    const salesUnitPopup: HTMLElement | null = this.getSalesUnitPopup(container);
    const button: HTMLElement | null = salesUnitPopup?.querySelector('[data-container="addtocart-btn"]') ?? null;

    if (!salesUnitPopup) {
      this.openerElement = null;
      return; // add snackbar
    }

    salesUnitPopup.setAttribute('data-salesunit-closing', '');
    if (restoreFocus) {
      this.restoreFocusToOpener();
    }

    animationEnded(salesUnitPopup, () => {
      salesUnitPopup.removeAttribute('data-salesunit-closing');
      salesUnitPopup.removeAttribute('salesunit-open');
      container.classList.remove('salesunit-open');

      if (isMobile()) {
        document.body.classList.remove('noof');
        document.body.classList.remove('salesunit-overlay-active');
        button?.remove();
      }
    });
  };

  // ================================
  // Helpers
  // ================================

  getActiveProductCard = (target: HTMLElement): HTMLElement | null => target?.closest('[data-container="product-card"]') ?? null;
  getSalesUnitPopup = (element: HTMLElement): HTMLElement | null => element?.querySelector('[data-container="salesunit-popup"]') ?? null;
  getAvailableSalesUnits = (element: HTMLElement): HTMLElement | null => element?.querySelector('.selector') ?? null;
  getIsCompactProductCard = (element: HTMLElement | null): boolean => element?.hasAttribute('product-card-compact') ?? false;
  useInlineSalesUnitMenuOnMobile = (element: HTMLElement | null): boolean => element?.dataset.salesunitMenuMobile === 'inline';

  getOpenSalesUnitProductCardContainer = (): HTMLElement | null => {
    const container = document.querySelector('[salesunit-open]');
    return container?.closest('[data-container="product-card"]') ?? null;
  };
}

export interface SalesUnitElement {
  element: HTMLElement;
  productId: string;
  salesUnitCode: string;
  salesUnit: number;
  unitPrice: string;
  upsellId: string;
}

export const SALES_UNITS_CLOSE = 'sales-units-close';
export const SALES_UNITS_OPEN = 'sales-units-open';
